import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Radar, AlertTriangle, Loader2, ArrowRight } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SEV_COLOR = {
  "crítica": "#ef4444",
  "alta": "#f97316",
  "media": "#f59e0b",
  "info": "#64748b",
};

export function VigilanciaInicioWidget() {
  const token = localStorage.getItem("token") || sessionStorage.getItem("token");
  const [signals, setSignals] = useState([]);
  const [criticalCount, setCriticalCount] = useState(0);
  const [projects, setProjects] = useState({});
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    let c = false;
    (async () => {
      try {
        // El contador usa /vt/dashboard (misma fuente que /vigilancia "Señales críticas"),
        // no el array truncado de abajo — antes el badge mostraba length tras un slice(0,6)
        // (máximo 6 aunque hubiera 93 reales). Fix 2026-08-23.
        const [dash, sr, pr] = await Promise.all([
          fetch(`${API}/vt/dashboard`, { headers: { Authorization: `Bearer ${token}` } }).then(r => r.json()),
          fetch(`${API}/vt/signals?status=abierta&limit=200`, { headers: { Authorization: `Bearer ${token}` } }).then(r => r.json()),
          fetch(`${API}/pg07/projects`, { headers: { Authorization: `Bearer ${token}` } }).then(r => r.json()),
        ]);
        if (c) return;
        setCriticalCount(dash?.totals?.signals_critical || 0);
        const crit = (Array.isArray(sr) ? sr : []).filter(s => s.severity === "crítica" || s.severity === "alta").slice(0, 6);
        setSignals(crit);
        const map = {};
        (Array.isArray(pr) ? pr : []).forEach(p => { map[p.id] = p.name; });
        setProjects(map);
      } finally { if (!c) setLoading(false); }
    })();
    return () => { c = true; };
  }, [token]);

  if (loading) return (
    <div style={{ padding: 16, borderRadius: 12, background: "rgba(6,182,212,0.05)", border: "1px solid rgba(6,182,212,0.2)", marginBottom: 16 }} data-testid="vt-inicio-widget-loading">
      <Loader2 style={{ width: 16, height: 16, color: "#06b6d4" }} className="animate-spin" />
    </div>
  );

  if (!criticalCount) return null;

  return (
    <div
      data-testid="vt-inicio-widget"
      style={{
        padding: 16,
        borderRadius: 12,
        background: "linear-gradient(135deg, rgba(6,182,212,0.08), rgba(239,68,68,0.06))",
        border: "1px solid rgba(6,182,212,0.3)",
        marginBottom: 16,
        cursor: "pointer",
      }}
      onClick={() => navigate("/vigilancia")}
      onKeyDown={(e) => { if (e.key === "Enter") navigate("/vigilancia"); }}
      role="button"
      tabIndex={0}
    >
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 10 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <Radar style={{ width: 18, height: 18, color: "#06b6d4" }} />
          <div>
            <div style={{ fontSize: 12, fontWeight: 700, color: "#f1f5f9" }} data-testid="vt-inicio-critical-count">
              Vigilancia Tecnológica
              <span style={{ marginLeft: 8, padding: "2px 8px", borderRadius: 10, background: "rgba(239,68,68,0.2)", color: "#fca5a5", fontSize: 10, fontWeight: 800 }}>
                <AlertTriangle style={{ width: 10, height: 10, display: "inline", marginRight: 2, verticalAlign: "middle" }} />
                {criticalCount} señal{criticalCount === 1 ? "" : "es"} crítica{criticalCount === 1 ? "" : "s"}
              </span>
            </div>
            <div style={{ fontSize: 10, color: "rgba(241,245,249,0.5)", marginTop: 2 }}>Requieren tu atención · mostrando {signals.length}</div>
          </div>
        </div>
        <ArrowRight style={{ width: 14, height: 14, color: "#06b6d4" }} />
      </div>

      <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
        {signals.map(s => {
          const row = (
            <>
              <span style={{ fontSize: 9, fontWeight: 800, padding: "2px 6px", background: SEV_COLOR[s.severity], color: "#0b1220", borderRadius: 4, textTransform: "uppercase" }}>{s.severity}</span>
              <span style={{ fontSize: 11, color: "#f1f5f9", flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={s.title}>{s.title}</span>
              <span style={{ fontSize: 10, color: "rgba(241,245,249,0.4)", maxWidth: 160, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={projects[s.project_id]}>{projects[s.project_id] || "—"}</span>
              {s.source_url && <ArrowRight style={{ width: 12, height: 12, color: SEV_COLOR[s.severity] || "#f59e0b", flexShrink: 0 }} />}
            </>
          );
          const rowStyle = { display: "flex", alignItems: "center", gap: 8, padding: "6px 10px", background: "rgba(15,23,42,0.4)", borderRadius: 8, borderLeft: `3px solid ${SEV_COLOR[s.severity] || "#f59e0b"}`, textDecoration: "none" };
          return s.source_url ? (
            <a key={s.id} href={s.source_url} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()} data-testid={`vt-inicio-signal-${s.id}`} style={rowStyle} title={`Abrir fuente en nueva pestaña`}>{row}</a>
          ) : (
            <div key={s.id} data-testid={`vt-inicio-signal-${s.id}`} style={rowStyle}>{row}</div>
          );
        })}
      </div>
    </div>
  );
}
